// App-side data for the canvas minimap: per-tile city/unit details enriched client-side from
// the main snapshot (no new WASM fields), and the translated label set.

import type { GameSnapshot } from '@aod/protocol';
import {
  type MinimapCityInfo,
  type MinimapDetails,
  type MinimapLabels,
  type MinimapUnitInfo,
  minimapTileKey,
} from '@aod/ui';
import { useMemo } from 'react';
import { type Translate, useT } from '../i18n/index.js';
import { cityOwnerId, isNeutralProps } from '../ownership.js';
import { merchantColour, merchantHouseOf } from '../sea/merchantModel.js';

/**
 * City + unit facts per tile. Owner ids are the sim's player ids, the same id space as the
 * minimap tiles' `controlledBy` (null = unclaimed) and the HUD palette (human = 0).
 * Cheap (one pass over the scene) and memoised on the snapshot. */
export function buildMinimapDetails(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): MinimapDetails {
  const cities = new Map<number, MinimapCityInfo>();
  const units = new Map<number, MinimapUnitInfo>();
  for (const n of snapshot?.scene.children ?? []) {
    const q = n.props?.q as number | undefined;
    const r = n.props?.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const key = minimapTileKey(q, r);
    if (n.type === 'city') {
      const neutral = isNeutralProps(n.props);
      cities.set(key, {
        ownerId: cityOwnerId(n.props) ?? 0,
        name: n.name,
        tier: n.props?.tier as number | undefined,
        isCapital: n.props?.isCapital === true,
        neutral,
      });
    } else if (n.type === 'unit') {
      const owner = (n.props?.ownerId as number | undefined) ?? 0;
      const prev = units.get(key);
      // A foreign house's vessel paints its own marker — the same colour as
      // its hull, so the minimap and the board agree.
      const house = merchantHouseOf(owner, n.props?.seaHouse as number | undefined);
      units.set(key, {
        // The first unit on a tile decides the dot colour.
        ownerId: prev?.ownerId ?? owner,
        count: (prev?.count ?? 0) + 1,
        ownCount: (prev?.ownCount ?? 0) + (owner === localPlayerIndex ? 1 : 0),
        color: prev?.color ?? (house !== null ? merchantColour(house) : undefined),
      });
    }
  }
  return { cities, units };
}

const BIOME_IDS = ['plains', 'forest', 'hills', 'mountain', 'desert', 'tundra', 'coast', 'ocean'];

export function minimapLabels(t: Translate): MinimapLabels {
  const biomes: Record<string, string> = {
    river: t('minimap.biome.river'),
  };
  for (const id of BIOME_IDS) biomes[id] = t(`biome.${id}` as 'biome.plains');
  return {
    title: t('minimap.title'),
    ariaLabel: t('minimap.aria'),
    summary: t('minimap.summary'),
    zoomIn: t('minimap.zoomIn'),
    cameraBox: t('minimap.cameraBox'),
    zoomOut: t('minimap.zoomOut'),
    fit: t('minimap.fit'),
    legend: t('minimap.legend'),
    legendTitle: t('minimap.legendTitle'),
    tileUnexplored: t('minimap.unexplored'),
    tileCoords: t('minimap.coords'),
    owner: t('minimap.owner'),
    unclaimed: t('minimap.unclaimed'),
    player: t('common.player'),
    you: t('minimap.you'),
    city: t('minimap.city'),
    cityNamed: t('minimap.cityNamed'),
    cityTier: t('minimap.cityTier'),
    capital: t('minimap.capital'),
    village: t('minimap.village'),
    unitsOwn: t('minimap.unitsOwn'),
    unitsOther: t('minimap.unitsOther'),
    explored: t('minimap.explored'),
    fog: t('minimap.fog'),
    unit: t('minimap.unit'),
    border: t('minimap.border'),
    marker: t('minimap.marker'),
    layers: t('minimap.layers'),
    layerTerritory: t('minimap.layerTerritory'),
    layerFog: t('minimap.layerFog'),
    layerUnits: t('minimap.layerUnits'),
    layerRivers: t('minimap.layerRivers'),
    layerRelief: t('minimap.layerRelief'),
    size: t('minimap.size'),
    sizeS: t('minimap.sizeS'),
    sizeM: t('minimap.sizeM'),
    sizeL: t('minimap.sizeL'),
    collapse: t('minimap.collapse'),
    expand: t('minimap.expand'),
    biomes,
  };
}

/** Translated minimap labels, stable per locale. */
export function useMinimapLabels(): MinimapLabels {
  const t = useT();
  return useMemo(() => minimapLabels(t), [t]);
}

/** Minimap details memoised on the snapshot. */
export function useMinimapDetails(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): MinimapDetails {
  return useMemo(
    () => buildMinimapDetails(snapshot, localPlayerIndex),
    [snapshot, localPlayerIndex],
  );
}

/**
 * Optional Minimap props the app adds: city/unit details, translated labels and the local player.
 * `playerColors` is deliberately not passed: the minimap uses the 3D board's palette
 * (`playerColor` by player id) so owners read the same on both; the HUD palette differs.
 */
export function useMinimapExtras(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): { details: MinimapDetails; labels: MinimapLabels; localPlayer: number } {
  const details = useMinimapDetails(snapshot, localPlayerIndex);
  const labels = useMinimapLabels();
  return useMemo(
    () => ({ details, labels, localPlayer: localPlayerIndex }),
    [details, labels, localPlayerIndex],
  );
}
